'use client';

import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';

interface GridPendingChangesBarProps {
  count: number;
  isSaving: boolean;
  onDiscard: () => void;
  onSave: () => void;
}

/** Sticky bar for the draft of pending grid moves. Hides itself once there's nothing pending. */
export function GridPendingChangesBar({
  count,
  isSaving,
  onDiscard,
  onSave,
}: GridPendingChangesBarProps) {
  if (count === 0) return null;

  return (
    <div
      className={cn(
        '-mx-4 flex flex-col gap-2 border-t bg-white px-4 py-3 shadow-[0_-2px_8px_rgba(0,0,0,0.06)] sm:flex-row sm:items-center sm:justify-between',
        // The support banner is fixed to bottom-0 with a higher z-index, and
        // the bottom padding the Layout adds while impersonating moves flow
        // content only — a sticky bar still parks at the viewport bottom, under
        // the banner, hiding the only way to commit the pending moves.
        // `--support-banner-h` is 0px outside a support session, so this is the
        // usual bottom-0 there.
        'sticky z-10 bottom-[var(--support-banner-h)]',
      )}
    >
      <div>
        <p className="text-sm font-medium">
          {count === 1 ? '1 cambio sin guardar' : `${count} cambios sin guardar`}
        </p>
        <p className="text-xs text-gray-500">
          Se les avisa a los jugadores al guardar.
        </p>
      </div>
      <div className="flex gap-2">
        <Button variant="outline" size="sm" onClick={onDiscard} disabled={isSaving}>
          Descartar
        </Button>
        <Button size="sm" onClick={onSave} disabled={isSaving} className="min-w-[100px]">
          {isSaving ? 'Guardando...' : 'Guardar'}
        </Button>
      </div>
    </div>
  );
}
